import { CuisineCategoryType, EditableFoodItemFragment } from 'src/gqlReactTypings.generated.d';

export const DEFAULT_PRIMARY_CUISINES = [
  'AMERICAN',
  'BAKED_GOODS',
  'CHINESE',
  'FRENCH',
  'INDIAN',
  'ITALIAN',
  'KOREAN',
  'LATIN_AMERICAN',
  'MEDITERRANEAN',
  'MEXICAN',
  'MIDDLE_EASTERN',
  'SOUTHEAST_ASIAN',
];

export const PRIMARY_CUISINES = [
  'AFRICAN',
  'AMERICAN',
  'ASIAN',
  'BAKED_GOODS',
  'CARIBBEAN',
  'CHINESE',
  'EUROPEAN',
  'FRENCH',
  'FUSION',
  'INDIAN',
  'INTERNATIONAL',
  'ITALIAN',
  'JAPANESE',
  'KOREAN',
  'LATIN_AMERICAN',
  'MEDITERRANEAN',
  'MEXICAN',
  'MIDDLE_EASTERN',
  'NATIVE_AMERICAN',
  'SOUTH_ASIAN',
  'SOUTH_PACIFIC',
  'SOUTHEAST_ASIAN',
  'SOUTHERN',
  'EASTERN_EUROPEAN',
];

export const GEOGRAPHICAL_CUISINE_TYPES = [
  CuisineCategoryType.Region,
  CuisineCategoryType.RegionCountry,
  CuisineCategoryType.RegionSubregion,
];

export const SPECIALTY_CUISINE_TYPES = [CuisineCategoryType.Dietary, CuisineCategoryType.FoodStyle];

export const SPECIALTY_CUISINE_DISH_TYPES = [CuisineCategoryType.FoodStyle, CuisineCategoryType.FoodItemSpecialty];

export enum AllergenTag {
  Dairy = 'CONTAINS_DAIRY',
  Eggs = 'CONTAINS_EGGS',
  Gluten = 'CONTAINS_GLUTEN',
  Nuts = 'CONTAINS_NUTS',
  Shellfish = 'CONTAINS_SHELLFISH',
  Soy = 'CONTAINS_SOY',
}

export enum DietaryTag {
  Halal = 'HALAL',
  Kosher = 'KOSHER',
  Keto = 'KETO',
  LowCarb = 'LOW_CARB',
  LowFodmap = 'LOW_FODMAP',
  LowSodium = 'LOW_SODIUM',
  Organic = 'ORGANIC',
  Paleo = 'PALEO',
  Vegan = 'VEGAN',
  Vegetarian = 'VEGETARIAN',
  Whole30 = 'WHOLE_30',
}

export const ALLERGEN_TAGS = Object.values(AllergenTag);
export const DIETARY_TAGS = Object.values(DietaryTag);

export const ALLERGEN_TAG_LIST = ALLERGEN_TAGS.map((tag) => `${tag}`);
export const DIETARY_TAG_LIST = DIETARY_TAGS.map((tag) => `${tag}`);
export const ALLERGEN_AND_DIETARY_TAG_LIST = [...ALLERGEN_TAG_LIST, ...DIETARY_TAG_LIST];

/**
 * Updates the allergen and dietary tags in a list of tags without changing other tags.
 * @param currentTags List of tags to update allergen/dietary tags from.
 * @param newTags Updated list of allergen/dietary tags.
 */
export const updateAllergenAndDietaryTags = (currentTags: string[], newTags: string[]) => {
  const filteredTags = currentTags.filter(
    (tag) => !ALLERGEN_AND_DIETARY_TAG_LIST.includes(tag) || newTags.includes(tag)
  );
  const unfilteredTags = newTags.filter((tag) => !filteredTags.includes(tag));
  return [...filteredTags, ...unfilteredTags];
};

/**
 * Updates dish tags in a list of tags without changing allergen and dietary tags.
 * @param currentTags List of tags to update dish tags from.
 * @param newTags Updated list of dish tags.
 */
export const updateDishTags = (currentTags: string[], newTags: string[]) => {
  const filteredTags = currentTags.filter((tag) => ALLERGEN_AND_DIETARY_TAG_LIST.includes(tag));
  const unfilteredTags = newTags.filter((tag) => !filteredTags.includes(tag));
  return [...filteredTags, ...unfilteredTags];
};

export const getAllergenAndDietaryTags = (tags: string[]) =>
  tags.filter((tag) => ALLERGEN_AND_DIETARY_TAG_LIST.includes(tag));
export const getDishTags = (tags: string[]) => tags.filter((tag) => !ALLERGEN_AND_DIETARY_TAG_LIST.includes(tag));

// Change Dietary Tag Functions.
export type ChangeDietaryTagProps = Parameters<{ (tags: string[], value: boolean) }>;

export const changeDietaryTag = (dietaryTag: AllergenTag | DietaryTag, tags: string[], value: boolean) =>
  value ? [...tags, dietaryTag] : tags.filter((tag) => tag !== dietaryTag);

export const changeAllergenDairy = (...props: ChangeDietaryTagProps) => changeDietaryTag(AllergenTag.Dairy, ...props);
export const changeAllergenEggs = (...props: ChangeDietaryTagProps) => changeDietaryTag(AllergenTag.Eggs, ...props);
export const changeAllergenGluten = (...props: ChangeDietaryTagProps) => changeDietaryTag(AllergenTag.Gluten, ...props);
export const changeAllergenNuts = (...props: ChangeDietaryTagProps) => changeDietaryTag(AllergenTag.Nuts, ...props);
export const changeAllergenShellfish = (...props: ChangeDietaryTagProps) =>
  changeDietaryTag(AllergenTag.Shellfish, ...props);
export const changeAllergenSoy = (...props: ChangeDietaryTagProps) => changeDietaryTag(AllergenTag.Soy, ...props);

export const changeDietaryHalal = (...props: ChangeDietaryTagProps) => changeDietaryTag(DietaryTag.Halal, ...props);
export const changeDietaryKosher = (...props: ChangeDietaryTagProps) => changeDietaryTag(DietaryTag.Kosher, ...props);
export const changeDietaryKeto = (...props: ChangeDietaryTagProps) => changeDietaryTag(DietaryTag.Keto, ...props);
export const changeDietaryLowCarb = (...props: ChangeDietaryTagProps) => changeDietaryTag(DietaryTag.LowCarb, ...props);
export const changeDietaryLowFodmap = (...props: ChangeDietaryTagProps) =>
  changeDietaryTag(DietaryTag.LowFodmap, ...props);
export const changeDietaryLowSodium = (...props: ChangeDietaryTagProps) =>
  changeDietaryTag(DietaryTag.LowSodium, ...props);
export const changeDietaryOrganic = (...props: ChangeDietaryTagProps) => changeDietaryTag(DietaryTag.Organic, ...props);
export const changeDietaryPaleo = (...props: ChangeDietaryTagProps) => changeDietaryTag(DietaryTag.Paleo, ...props);
export const changeDietaryVegan = (...props: ChangeDietaryTagProps) => changeDietaryTag(DietaryTag.Vegan, ...props);
export const changeDietaryVegetarian = (...props: ChangeDietaryTagProps) =>
  changeDietaryTag(DietaryTag.Vegetarian, ...props);
export const changeDietaryWhole30 = (...props: ChangeDietaryTagProps) => changeDietaryTag(DietaryTag.Whole30, ...props);

// Has dietary tag functions.
export type HasDietaryTagProps = Parameters<{ (tags: string[]) }>;
export const hasDietaryTag = (dietaryTag: AllergenTag | DietaryTag, tags: string[]) => tags.includes(dietaryTag);

export const hasAllergenDairy = (...props: HasDietaryTagProps) => hasDietaryTag(AllergenTag.Dairy, ...props);
export const hasAllergenEggs = (...props: HasDietaryTagProps) => hasDietaryTag(AllergenTag.Eggs, ...props);
export const hasAllergenGluten = (...props: HasDietaryTagProps) => hasDietaryTag(AllergenTag.Gluten, ...props);
export const hasAllergenNuts = (...props: HasDietaryTagProps) => hasDietaryTag(AllergenTag.Nuts, ...props);
export const hasAllergenShellfish = (...props: HasDietaryTagProps) => hasDietaryTag(AllergenTag.Shellfish, ...props);
export const hasAllergenSoy = (...props: HasDietaryTagProps) => hasDietaryTag(AllergenTag.Soy, ...props);

export const hasDietaryHalal = (...props: HasDietaryTagProps) => hasDietaryTag(DietaryTag.Halal, ...props);
export const hasDietaryKosher = (...props: HasDietaryTagProps) => hasDietaryTag(DietaryTag.Kosher, ...props);
export const hasDietaryKeto = (...props: HasDietaryTagProps) => hasDietaryTag(DietaryTag.Keto, ...props);
export const hasDietaryLowCarb = (...props: HasDietaryTagProps) => hasDietaryTag(DietaryTag.LowCarb, ...props);
export const hasDietaryLowFodmap = (...props: HasDietaryTagProps) => hasDietaryTag(DietaryTag.LowFodmap, ...props);
export const hasDietaryLowSodium = (...props: HasDietaryTagProps) => hasDietaryTag(DietaryTag.LowSodium, ...props);
export const hasDietaryOrganic = (...props: HasDietaryTagProps) => hasDietaryTag(DietaryTag.Organic, ...props);
export const hasDietaryPaleo = (...props: HasDietaryTagProps) => hasDietaryTag(DietaryTag.Paleo, ...props);
export const hasDietaryVegan = (...props: HasDietaryTagProps) => hasDietaryTag(DietaryTag.Vegan, ...props);
export const hasDietaryVegetarian = (...props: HasDietaryTagProps) => hasDietaryTag(DietaryTag.Vegetarian, ...props);
export const hasDietaryWhole30 = (...props: HasDietaryTagProps) => hasDietaryTag(DietaryTag.Whole30, ...props);

export const getFoodItemValuesFromTags = (tags: string[]): Partial<EditableFoodItemFragment> => ({
  dairyFree: !tags.includes(AllergenTag.Dairy),
  glutenFree: !tags.includes(AllergenTag.Gluten),
  isHalal: tags.includes(DietaryTag.Halal),
  isOrganic: tags.includes(DietaryTag.Organic),
  lowFodmap: tags.includes(DietaryTag.LowFodmap),
  paleo: tags.includes(DietaryTag.Paleo),
  vegan: tags.includes(DietaryTag.Vegan),
  vegetarian: tags.includes(DietaryTag.Vegetarian),
  whole30: tags.includes(DietaryTag.Whole30),
});
